import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Target, Save, Trash2, Loader2, SlidersHorizontal } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function SalesTargetsPage() {
  const [month, setMonth] = useState(thisMonth());
  const [employees, setEmployees] = useState([]);
  const [targets, setTargets] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, t, c] = await Promise.all([
        axios.get(`${API}/users`, auth()),
        axios.get(`${API}/sales/targets?month=${month}`, auth()),
        axios.get(`${API}/sales/config`, auth()),
      ]);
      setEmployees((u.data || []).filter(e => e.is_active !== false));
      setTargets(t.data || []);
      setConfig(c.data);
    } catch (e) {
      toast.error('Failed to load targets');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const targetFor = (empId) => targets.find(t => t.employee_id === empId);

  const saveTarget = async (emp) => {
    const d = drafts[emp.id] || {};
    const existing = targetFor(emp.id);
    const amount = d.target_amount ?? existing?.target_amount;
    if (!amount) { toast.error('Enter a target amount'); return; }
    try {
      await axios.post(`${API}/sales/targets`, {
        employee_id: emp.id, month,
        target_amount: parseFloat(amount),
        minimum_eligibility_amount: parseFloat(d.min ?? existing?.minimum_eligibility_amount ?? 0) || null,
        notes: d.notes ?? existing?.notes ?? '',
      }, auth());
      toast.success(`Target saved for ${emp.full_name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save target'); }
  };

  const removeTarget = async (empId) => {
    const t = targetFor(empId);
    if (!t) return;
    try {
      await axios.delete(`${API}/sales/targets/${t.id}`, auth());
      toast.success('Target removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      await axios.put(`${API}/sales/config`, config, auth());
      toast.success('Slab & rule config saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save config'); }
    finally { setSavingCfg(false); }
  };

  const setSlab = (i, key, val) => {
    setConfig(prev => {
      const slabs = [...prev.slabs];
      slabs[i] = { ...slabs[i], [key]: val === '' ? null : parseFloat(val) };
      return { ...prev, slabs };
    });
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-wt-gold" /></div>;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="sales-targets-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-wt-text-primary flex items-center gap-2">
            <Target className="w-7 h-7 text-wt-gold" /> Sales Targets
          </h1>
          <p className="text-sm text-wt-text-muted">Monthly targets, incentive slabs & discount rules</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="w-44 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="targets-month-picker" />
      </div>

      <Card className="bg-wt-surface border-wt-border">
        <CardHeader><CardTitle className="text-wt-text-primary text-base">Employee Targets — {month}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {employees.map(emp => {
            const t = targetFor(emp.id);
            const d = drafts[emp.id] || {};
            return (
              <div key={emp.id} className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-wt-paper border border-wt-border" data-testid={`target-row-${emp.id}`}>
                <div className="w-44 min-w-0">
                  <p className="text-sm text-wt-text-primary truncate">{emp.full_name}</p>
                  <p className={`text-[11px] ${emp.incentive_enabled ? 'text-green-400' : 'text-wt-text-muted'}`}>
                    {emp.incentive_enabled ? 'Incentive enabled' : 'Incentive disabled'}
                  </p>
                </div>
                <Input type="number" placeholder={`Target (default ${config?.default_target || 80000})`}
                  value={d.target_amount ?? t?.target_amount ?? ''}
                  onChange={(e) => setDrafts(p => ({ ...p, [emp.id]: { ...p[emp.id], target_amount: e.target.value } }))}
                  className="w-36 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" data-testid={`target-amount-${emp.id}`} />
                <Input type="number" placeholder={`Min elig (${config?.default_min_eligibility || 56000})`}
                  value={d.min ?? t?.minimum_eligibility_amount ?? ''}
                  onChange={(e) => setDrafts(p => ({ ...p, [emp.id]: { ...p[emp.id], min: e.target.value } }))}
                  className="w-36 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
                <Input placeholder="Notes" value={d.notes ?? t?.notes ?? ''}
                  onChange={(e) => setDrafts(p => ({ ...p, [emp.id]: { ...p[emp.id], notes: e.target.value } }))}
                  className="flex-1 min-w-[120px] h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
                <Button size="sm" className="h-8 bg-wt-gold text-black hover:bg-wt-gold/90" onClick={() => saveTarget(emp)} data-testid={`save-target-${emp.id}`}>
                  <Save className="w-3.5 h-3.5" />
                </Button>
                {t && (
                  <Button size="sm" variant="ghost" className="h-8 text-red-400" onClick={() => removeTarget(emp.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
          <p className="text-xs text-wt-text-muted">Ramp-up guide: Month 1 — {config?.ramp_up?.[0]?.toLocaleString()}, Month 2 — {config?.ramp_up?.[1]?.toLocaleString()}, Month 3+ — {config?.ramp_up?.[2]?.toLocaleString()}</p>
        </CardContent>
      </Card>

      {config && (
        <Card className="bg-wt-surface border-wt-border">
          <CardHeader>
            <CardTitle className="text-wt-text-primary text-base flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-wt-gold" /> Incentive Slabs & Rules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {config.slabs.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`slab-row-${i}`}>
                  <span className="text-wt-text-muted w-10">Slab {i + 1}</span>
                  <Input type="number" value={s.min ?? ''} onChange={(e) => setSlab(i, 'min', e.target.value)}
                    className="w-28 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
                  <span className="text-wt-text-muted">to</span>
                  <Input type="number" value={s.max ?? ''} placeholder="∞" onChange={(e) => setSlab(i, 'max', e.target.value)}
                    className="w-28 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
                  <span className="text-wt-text-muted">→</span>
                  <Input type="number" step="0.05" value={s.pct ?? ''} onChange={(e) => setSlab(i, 'pct', e.target.value)}
                    className="w-20 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
                  <span className="text-wt-text-muted">%</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-wt-text-muted">Default Target</Label>
                <Input type="number" value={config.default_target ?? ''} onChange={(e) => setConfig(p => ({ ...p, default_target: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Default Min Eligibility</Label>
                <Input type="number" value={config.default_min_eligibility ?? ''} onChange={(e) => setConfig(p => ({ ...p, default_min_eligibility: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Auto Discount Limit %</Label>
                <Input type="number" value={config.discount_rules?.auto_limit_pct ?? 5}
                  onChange={(e) => setConfig(p => ({ ...p, discount_rules: { ...p.discount_rules, auto_limit_pct: parseFloat(e.target.value) || 0 } }))}
                  className="mt-1 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
              </div>
              <div>
                <Label className="text-xs text-wt-text-muted">Manager Discount Limit %</Label>
                <Input type="number" value={config.discount_rules?.manager_limit_pct ?? 10}
                  onChange={(e) => setConfig(p => ({ ...p, discount_rules: { ...p.discount_rules, manager_limit_pct: parseFloat(e.target.value) || 0 } }))}
                  className="mt-1 h-8 bg-wt-bg border-wt-border text-wt-text-primary text-sm" />
              </div>
            </div>
            <p className="text-xs text-wt-text-muted">Discounts ≤ auto limit are approved automatically; up to manager limit need manager approval; above that need admin approval. Unapproved heavy-discount orders are excluded from incentives.</p>
            <Button onClick={saveConfig} disabled={savingCfg} className="bg-wt-gold text-black hover:bg-wt-gold/90" data-testid="save-config-btn">
              {savingCfg && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save Slabs & Rules
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
